import React from "react";

interface PaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  className?: string;
  showTotal?: boolean;
  // When provided, a "Rows per page" selector is shown and the controls switch
  // to a split layout: total + rows-per-page on the left, page numbers on the right.
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}

// Windowed page numbers: show up to 5, centered on the current page.
const getPageNumbers = (currentPage: number, totalPages: number): number[] => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  let start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, start + 4);
  if (end === totalPages) start = Math.max(1, end - 4);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
  className = "flex flex-wrap items-center justify-center my-4 sm:my-6 gap-2 sm:gap-3 text-sm px-3",
  showTotal = false,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
}) => {
  const totalPages = Math.ceil((totalCount || 0) / pageSize);
  const atStart = currentPage === 1;
  const atEnd = currentPage >= totalPages;

  // The page-number list items, shared by both layouts.
  const pageItems = (
    <>
      <li>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={atStart}
          className={`flex items-center justify-center w-8 h-8 leading-tight rounded-md ${
            atStart
              ? "text-gray-300 border border-gray-300 cursor-not-allowed"
              : "text-primaryblue-50 border border-primaryblue-50 cursor-pointer"
          }`}
        >
          <span className="sr-only">Previous</span>
          <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
        </button>
      </li>

      {getPageNumbers(currentPage, totalPages).map((pageNumber) => (
        <li key={pageNumber}>
          <button
            onClick={() => onPageChange(pageNumber)}
            className={`flex items-center justify-center w-8 h-8 rounded-md ${
              currentPage === pageNumber
                ? "text-white border border-primaryblue-50 bg-primaryblue-50"
                : "text-primaryblue-50 border border-primaryblue-50"
            }`}
          >
            {pageNumber}
          </button>
        </li>
      ))}

      <li>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={atEnd}
          className={`flex items-center justify-center w-8 h-8 rounded-md ${
            atEnd
              ? "text-gray-300 border border-gray-300 cursor-not-allowed"
              : "text-primaryblue-50 border border-primaryblue-50 cursor-pointer"
          }`}
        >
          <span className="sr-only">Next</span>
          <i className="st-arrow-right text-xl font-semibold"></i>
        </button>
      </li>
    </>
  );

  // Split layout (Tokens-style): rows-per-page on the left, page numbers on the right.
  if (onPageSizeChange) {
    return (
      <div className="flex flex-row flex-wrap items-center justify-between gap-3 my-4 sm:my-6 px-3 text-sm">
        <div className="flex items-center justify-start gap-2 text-lightgray-200 order-2 sm:order-1">
          {showTotal && totalCount > 0 && (
            <span className="whitespace-nowrap">
              Total {totalCount} {totalCount === 1 ? "record" : "records"}
            </span>
          )}
          <label htmlFor="rows-per-page" className="whitespace-nowrap">
            Rows per page
          </label>
          <select
            id="rows-per-page"
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              // Drop focus so the select doesn't stay highlighted after the
              // list reloads (matches the Tokens page behaviour).
              e.target.blur();
            }}
            className="h-8 rounded-md border border-primaryblue-50 text-primaryblue-50 bg-white pl-3 pr-7 cursor-pointer focus:outline-none"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <nav className="order-1 sm:order-2">
          <ul className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-sm">
            {pageItems}
          </ul>
        </nav>
      </div>
    );
  }

  // Default layout: optional total above, controls centered.
  return (
    <nav>
      {showTotal && totalCount > 0 && (
        <p className="text-center text-lightgray-200 text-sm mb-2">
          Total {totalCount} {totalCount === 1 ? "record" : "records"}
        </p>
      )}
      <ul className={className}>{pageItems}</ul>
    </nav>
  );
};

export default Pagination;
